"use client";
import { jsx as _jsx } from "react/jsx-runtime";
import React from "react";
import { classNames } from "../../utilities/props.js";
import useIsomorphicLayoutEffect from "../../hooks/useIsomorphicLayoutEffect.js";
import { ThemeContext } from "./Theme.context.js";
import { getRootThemeEl } from "./Theme.utilities.js";
import { useTheme, useGlobalColorMode } from "./useTheme.js";
import s from "./Theme.module.css";
const Theme = (props) => _jsx(PrivateTheme, { ...props });
export const PrivateTheme = (props) => {
    const { name, defaultName, colorMode, scoped, scopeRef, children, className } = props;
    const [mounted, setMounted] = React.useState(false);
    const [stateTheme, setStateTheme] = React.useState(defaultName);
    const globalColorMode = useGlobalColorMode();
    const parentTheme = useTheme();
    const isRootProvider = !parentTheme.theme;
    const usedTheme = name || stateTheme || parentTheme.theme;
    const rootTheme = isRootProvider || scoped ? usedTheme : parentTheme.rootTheme;
    const parentColorMode = isRootProvider || scoped ? globalColorMode.mode : parentTheme.colorMode;
    const invertedColorMode = parentColorMode === "light" ? "dark" : "light";
    const usedColorMode = colorMode === "inverted" ? invertedColorMode : colorMode || parentColorMode;
    const rootClassNames = classNames(s.root, className);
    const setRootTheme = React.useCallback((theme) => {
        if (isRootProvider) {
            setStateTheme(theme);
        }
        else {
            parentTheme.setRootTheme(theme);
        }
    }, [isRootProvider, parentTheme]);
    const setTheme = React.useCallback((theme) => {
        setStateTheme(theme);
    }, []);
    useIsomorphicLayoutEffect(() => {
        setMounted(true);
    }, []);
    useIsomorphicLayoutEffect(() => {
        if (!document || !isRootProvider)
            return;
        const themeRootEl = getRootThemeEl(scopeRef?.current);
        const hasColorModeApplied = themeRootEl.getAttribute("data-rs-color-mode");
        themeRootEl.setAttribute("data-rs-theme", usedTheme);
        if (!hasColorModeApplied)
            themeRootEl.setAttribute("data-rs-color-mode", usedColorMode);
        return () => {
            themeRootEl.removeAttribute("data-rs-theme");
            if (!hasColorModeApplied)
                themeRootEl.removeAttribute("data-rs-color-mode");
        };
    }, [usedTheme, usedColorMode, isRootProvider, scopeRef]);
    const value = React.useMemo(() => ({
        theme: usedTheme,
        rootTheme,
        colorMode: usedColorMode,
        setTheme,
        setRootTheme,
    }), [usedTheme, usedColorMode, setTheme, setRootTheme, rootTheme]);
    return (_jsx(ThemeContext.Provider, { value: value, children: _jsx("div", { className: rootClassNames, ref: scopeRef, "data-rs-root": scoped ? true : undefined, "data-rs-theme": isRootProvider ? undefined : usedTheme, "data-rs-color-mode": isRootProvider || (!colorMode && !mounted) ? undefined : usedColorMode, children: children }) }));
};
Theme.displayName = "Theme";
export default Theme;
